import { useState } from 'react';
import { Box, Container, Typography, Grid, Alert, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import PlanCard from '../components/PlanCard';
import api from '../services/api';
import { usePricing } from '../hooks/usePricing';
import { FREE_FEATURES, PREMIUM_FEATURES, getFormattedFeatures } from '../config/pricingFeatures';

function PlanSelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const selectedInterval = 'month'; // Locked to monthly only
  const { pricing, loading: pricingLoading } = usePricing();

  const isAuthenticated = keycloak?.authenticated || false;

  const handleSelectFree = () => {
    navigate('/home');
  };

  const handleSelectPremium = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/subscription/create-checkout-session', {
        interval: selectedInterval
      });
      window.location.href = response.data.checkoutUrl;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError(err.response?.data?.message || t('planSelection.checkoutError', 'Failed to start checkout. Please try again.'));
      setLoading(false);
    }
  };

  // Get the current price (monthly only)
  const getCurrentPrice = () => {
    if (pricingLoading) {
      return <Skeleton width="100px" />;
    }

    // Fallback price for monthly
    const getFallbackPrice = () => {
      return `39 SEK${t('pricing.billing.intervals.month')}`;
    };

    if (!pricing?.prices?.monthly) {
      return getFallbackPrice();
    }

    const currentPrice = pricing.prices.monthly;
    return `${currentPrice.formatted.amount} ${currentPrice.formatted.currency}${t(`pricing.billing.intervals.${currentPrice.interval}`)}`;
  };

  const freeFeatures = getFormattedFeatures(t, FREE_FEATURES);
  const premiumFeatures = getFormattedFeatures(t, PREMIUM_FEATURES);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        display: 'flex',
        alignItems: { xs: 'center', md: 'flex-start' },
        background: `
          radial-gradient(ellipse 80% 50% at 50% -20%, rgba(144, 202, 249, 0.15), transparent),
          radial-gradient(circle at 20% 80%, rgba(244, 143, 177, 0.08), transparent 50%),
          #212529
        `,
        py: { xs: 2, md: 2 },
        px: { xs: 2, md: 0 },
      }}
    >
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 1,
              color: '#FFFFFF',
            }}
          >
            {t('planSelection.title')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: '#ADB5BD',
              mb: 2,
            }}
          >
            {t('planSelection.subtitle')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#90caf9',
              fontSize: '0.9rem',
              mb: 3,
            }}
          >
            {t('planSelection.note')}
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={4} sx={{ alignItems: { xs: 'flex-start', md: 'stretch' }, mt: 1 }}>
          {/* Free Plan */}
          <Grid item xs={12} sm={6} sx={{ display: 'flex', order: { xs: 2, sm: 0 } }}>
            <PlanCard
              variant="free"
              name={t('planSelection.free.name')}
              price={t('planSelection.free.price')}
              features={freeFeatures}
              buttonText={isAuthenticated ? t('planSelection.free.ctaContinue') : t('planSelection.free.cta')}
              onButtonClick={handleSelectFree}
              buttonPosition="bottom"
              compactFeatures
            />
          </Grid>

          {/* Premium Plan */}
          <Grid item xs={12} sm={6} sx={{ display: 'flex', order: { xs: 1, sm: 0 } }}>
            <PlanCard
              variant="premium"
              name={t('planSelection.premium.name')}
              price={getCurrentPrice()}
              subtitle={t('planSelection.premium.subtitle')}
              features={premiumFeatures}
              buttonText={t('planSelection.premium.cta')}
              onButtonClick={handleSelectPremium}
              loading={loading}
              badge={t('planSelection.premium.badge')}
              buttonPosition="bottom"
              compactFeatures
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default PlanSelection;
